import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  type OnInit,
  type Signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { AccessControlService } from '@bizzdesign/auth';
import { FeatureFlagsStore } from '@bizzdesign/shared/feature-flags';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  type Action,
  type ActionState,
  type AppVersion,
  type BDMenuItem,
} from '@bizzdesign/shared/models';
import {
  copyValueToClipboard,
  type FeatureFlagKey,
} from '@bizzdesign/shared/utils';
import { ProductAnalyticsService } from '@bizzdesign/shared/services';
import { navigationMenu } from '@bizzdesign/styles';
import { RichTooltipDirective } from '@bizzdesign/shared/directives';
import { LogoComponent } from '@bizzdesign/ui-logo';
import { RichTooltipComponent } from '@bizzdesign/ui-rich-tooltip';
import { AccordionModule } from 'primeng/accordion';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { combineLatest, map, mergeMap, type Observable, of } from 'rxjs';
import {
  type SidebarConfig,
  type SidebarItem,
} from '../../utils/layout-config.utils';
import { createSupportMenuItems } from '../../utils/support-menu-items.utils';
import { TranslateMenuItemsPipe } from '@bizzdesign/shared/pipes';
import { isNotUndefinedOrNull } from '@bizzdesign/bizzdesign-sdk';
import { SidebarChildActivePipe } from './sidebar-child-active.pipe';

@Component({
  selector: 'bd-sidebar',
  imports: [
    AsyncPipe,
    NgClass,
    RouterModule,
    SharedI18nModule,
    AccordionModule,
    TooltipModule,
    LogoComponent,
    RichTooltipComponent,
    RichTooltipDirective,
    MenuModule,
    TranslateMenuItemsPipe,
    SidebarChildActivePipe,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit {
  private readonly accessControlService = inject(AccessControlService);
  private readonly featureFlags = inject(FeatureFlagsStore);
  private readonly productAnalyticsService = inject(ProductAnalyticsService);

  @Input() sidebarConfig: SidebarConfig = {};
  @Input() appVersion: AppVersion | undefined;

  filteredSidebarItems$: Observable<SidebarItem[]> = of([]);
  copyValueToClipboard = copyValueToClipboard;

  readonly supportMenuItems: BDMenuItem[] = createSupportMenuItems(
    this.productAnalyticsService,
  );
  readonly navigationMenuPreset = navigationMenu;
  readonly currentYear = new Date().getFullYear();

  ngOnInit() {
    this.filteredSidebarItems$ = this.getFilteredSidebarItems$();
  }

  private getFilteredSidebarItems$(): Observable<SidebarItem[]> {
    const items = this.filterItemsWithFeatureFlags(
      this.sidebarConfig.sidebarItems ?? [],
    );

    return items.length === 0
      ? of<SidebarItem[]>([])
      : combineLatest(
          items.map((item) => this.filterItemWithPermissions(item)),
        ).pipe(
          map((filtered): SidebarItem[] =>
            filtered.filter((item): item is SidebarItem => !!item),
          ),
        );
  }

  private filterItemsWithFeatureFlags(items: SidebarItem[]): SidebarItem[] {
    return items
      .filter(
        (item) =>
          !item.featureFlag ||
          !!(this.featureFlags as Record<FeatureFlagKey, Signal<unknown>>)[
            item.featureFlag
          ](),
      )
      .map((item) => {
        const filteredChildren = item.subItems
          ? this.filterItemsWithFeatureFlags(item.subItems)
          : [];
        return {
          ...item,
          subItems: filteredChildren,
        };
      });
  }

  private filterItemWithPermissions(
    item: SidebarItem,
  ): Observable<SidebarItem | null> {
    const permObservables = this.canAccess(item);
    const perms$ =
      permObservables.length === 0
        ? of(true)
        : combineLatest(permObservables).pipe(
            map((results) => results.every((state) => state === 'accessible')),
          );

    if (item.subItems?.length) {
      return combineLatest(
        item.subItems.map((subItem) => this.filterItemWithPermissions(subItem)),
      ).pipe(
        map((filteredSubItems) =>
          filteredSubItems.filter((filteredSubItem) => !!filteredSubItem),
        ),
        mergeMap((filteredSubItems) =>
          perms$.pipe(
            map((hasPerms) =>
              !hasPerms && filteredSubItems.length === 0
                ? null
                : { ...item, subItems: filteredSubItems },
            ),
          ),
        ),
      );
    } else {
      return perms$.pipe(map((hasPerms) => (hasPerms ? item : null)));
    }
  }

  private canAccess(sidebarItem: SidebarItem): Observable<ActionState>[] {
    return Object.entries(sidebarItem.permissions ?? {}).map(
      ([action, subject]) =>
        this.accessControlService.getActionState$(subject, action as Action),
    );
  }

  onItemClick(routerLink: string) {
    this.productAnalyticsService.sendEvent('navigated_from_sidebar', {
      to: routerLink,
    });
  }

  onVersionViewed() {
    if (isNotUndefinedOrNull(this.appVersion)) {
      this.productAnalyticsService.sendEvent('app_version_viewed');
    }
  }

  onLogoClick() {
    this.productAnalyticsService.sendEvent('logo_clicked');
  }
}
